// Multilingue FR (source, sans préfixe) / EN / DE / IT (préfixe d'URL /en, /de, /it).
// t("Texte français", { n: 3 }) : texte traduit (catalogue servi par le backend, traduit par IA + cache),
// à défaut le texte français. Les contenus du back-office arrivent déjà traduits via ?lang=.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link as RRLink, NavLink as RRNavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

export const DEFAULT_LANG = "fr";
export const LANGS = [
  { code: "fr", label: "Français", short: "FR", htmlLang: "fr", ogLocale: "fr_FR", intl: "fr-FR" },
  { code: "en", label: "English", short: "EN", htmlLang: "en", ogLocale: "en_GB", intl: "en-GB" },
  { code: "de", label: "Deutsch", short: "DE", htmlLang: "de", ogLocale: "de_DE", intl: "de-DE" },
  { code: "it", label: "Italiano", short: "IT", htmlLang: "it", ogLocale: "it_IT", intl: "it-IT" },
];
const PREFIXED = LANGS.filter((l) => l.code !== DEFAULT_LANG).map((l) => l.code);
export const langInfo = (code) => LANGS.find((l) => l.code === code) || LANGS[0];

// Marqueur pour l'extraction du catalogue (chaînes déclarées hors JSX) : renvoie la chaîne telle quelle.
export const N_ = (s) => s;

export function langFromPath(pathname = "/") {
  const seg = pathname.split("/")[1];
  return PREFIXED.includes(seg) ? seg : DEFAULT_LANG;
}
export function stripLang(pathname = "/") {
  const lang = langFromPath(pathname);
  if (lang === DEFAULT_LANG) return pathname || "/";
  const rest = pathname.slice(lang.length + 1);
  return rest || "/";
}
const NOT_LOCALIZED = /^\/(admin|apercu|api)(\/|$)/;
export function localizePath(path, lang) {
  if (typeof path !== "string" || !path.startsWith("/") || NOT_LOCALIZED.test(path)) return path;
  const base = stripLang(path);
  if (lang === DEFAULT_LANG) return base;
  return base === "/" ? `/${lang}` : base.startsWith("/?") ? `/${lang}${base.slice(1)}` : `/${lang}${base}`;
}

const fill = (s, vars) => (vars ? String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m)) : s);

// Accès hors React (formatage des dates, etc.)
let current = { lang: DEFAULT_LANG, dict: {} };
export const currentLang = () => current.lang;
export const currentIntl = () => langInfo(current.lang).intl;
export const tr = (s, vars) => fill(current.dict[s] || s, vars);

const LocaleContext = createContext({ lang: DEFAULT_LANG, t: (s, v) => fill(s, v), dict: {} });

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const storageKey = (lang) => `i18n:${lang}`;
function readStored(lang) {
  try { return JSON.parse(localStorage.getItem(storageKey(lang)) || "{}"); } catch { return {}; }
}

export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const [dicts, setDicts] = useState(() => (lang === DEFAULT_LANG ? {} : { [lang]: readStored(lang) }));

  useEffect(() => {
    document.documentElement.lang = langInfo(lang).htmlLang;
    if (lang === DEFAULT_LANG) return;
    let cancelled = false;
    setDicts((d) => (d[lang] ? d : { ...d, [lang]: readStored(lang) }));
    const load = (attempt = 0) => axios.get(`${API}/public/i18n/${lang}`).then((r) => {
      if (cancelled) return;
      const strings = r.data?.strings || {};
      setDicts((d) => ({ ...d, [lang]: strings }));
      try { localStorage.setItem(storageKey(lang), JSON.stringify(strings)); } catch { /* quota */ }
      // traductions en cours de génération côté serveur : nouvel essai plus tard
      if (Object.keys(strings).length === 0 && attempt < 3) setTimeout(() => load(attempt + 1), 15000);
    }).catch(() => {});
    load();
    return () => { cancelled = true; };
  }, [lang]);

  const dict = useMemo(() => (lang === DEFAULT_LANG ? {} : dicts[lang] || {}), [lang, dicts]);
  current = { lang, dict };
  const t = useCallback((s, vars) => fill(dict[s] || s, vars), [dict]);
  const value = useMemo(() => ({ lang, t, dict }), [lang, t, dict]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);
export const useT = () => useContext(LocaleContext).t;
export const useLang = () => useContext(LocaleContext).lang;
export function useLocalePath() {
  const lang = useLang();
  return useCallback((p) => localizePath(p, lang), [lang]);
}
export function useLocaleNavigate() {
  const navigate = useNavigate();
  const lp = useLocalePath();
  return useCallback((to, opts) => navigate(typeof to === "string" ? lp(to) : to, opts), [navigate, lp]);
}

// Liens internes automatiquement préfixés par la langue courante
export const Link = React.forwardRef(function LocaleLink({ to, ...rest }, ref) {
  const lp = useLocalePath();
  return <RRLink ref={ref} to={lp(to)} {...rest} />;
});
export const NavLink = React.forwardRef(function LocaleNavLink({ to, ...rest }, ref) {
  const lp = useLocalePath();
  return <RRNavLink ref={ref} to={lp(to)} {...rest} />;
});
